import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, useInView, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "./Header";
import InteractiveLines from "./InteractiveLines-w";
import Footer from "./Footer";

// Add animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const Witness: React.FC = () => {
  const { scrollY } = useScroll();
  const scrollX = useTransform(scrollY, [0, 400], [0, -200]);
  const xSpring = useSpring(scrollX, { stiffness: 100, damping: 30 });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const designImages = [
    "/w-1.png",
    "/w-3.png",
    "/w-5.png",
    "/w-7.png",
    "/w-12.png",
    "/w-13.png",
  ];
  
  const heroRef = useRef(null);
  const uiSectionRef = useRef(null);
  const uiSectionInView = useInView(uiSectionRef, { once: true, amount: 0.3 });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % designImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [designImages.length]);

  return (
    <div className="relative min-h-screen bg-[#F4F1EC]">
      <Header />
      <motion.section
        ref={heroRef}
        className="relative w-full min-h-[100vh] h-screen overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 z-0">
          <InteractiveLines />
        </div>
        <motion.h1
          className="relative z-10 text-[40vw] md:text-[35vw] lg:text-[30vw] font-serif font-bold text-right mt-[45vh] md:mt-[25vh] pr-[5vw] whitespace-nowrap"
          style={{ x: xSpring }}
        >
          WITNESS
        </motion.h1>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            className="relative z-50"
            animate={{
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <motion.img 
              src="/card3.png" 
              alt="Card" 
              className="w-72 h-auto"
              whileHover={{
                scale: 1.1,
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
            />
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[30vh] bg-gradient-to-t from-[#F4F1EC] to-transparent z-40"></div>
      </motion.section>

      {/* Interactive Demo Section */}
      <motion.section
        className="relative w-full min-h-screen bg-[#F4F1EC] py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8 -mt-20"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1 }}
      >
        <motion.div
          className="max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="w-40 sm:w-60 md:w-80 h-[2px] bg-[#F0584E] mb-6 sm:mb-8" />
          <motion.div variants={itemVariants} className="flex items-center gap-2 sm:gap-4 mb-6 sm:mb-8">
            <img src="/eye.png" alt="Eye Icon" className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold">App Walkthrough</h2>
          </motion.div>
          <motion.div
            variants={itemVariants}
            className="w-full aspect-video rounded-[32px] overflow-hidden shadow-2xl border-2 border-[#F0584E]/20"
          >
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/4H133GpUAsI"
              title="Witness Demo Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Gradient / Marquee + Rotating Images */}
      <motion.section
        ref={uiSectionRef}
        className="relative w-full h-[600px] sm:h-[700px] md:h-[800px] bg-gradient-to-b from-[#F0584E] via-[#F5F2ED] via-[#F5F2ED] via-[#F5F2ED] via-[#F5F2ED] to-[#F5F2ED] py-6 sm:py-8 md:py-10 px-4 sm:px-6 md:px-8 flex items-center justify-center"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: uiSectionInView ? 1 : 0, y: uiSectionInView ? 0 : 50 }}
        transition={{ duration: 1 }}
      >
        {/* Background animated circles */}
        {[...Array(5)].map((_, i) => {
          const size = Math.random() * 300 + 100;
          const scale = Math.random() * 0.3 + 0.7;
          const opacity = Math.random() * 0.2 + 0.1;
          return (
            <motion.div
              key={i}
              className="absolute bg-[#F0584E] rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${size}px`,
                height: `${size}px`,
              }}
              animate={{
                scale: [1, scale, 1],
                opacity: [opacity * 0.7, opacity, opacity * 0.7],
              }}
              transition={{
                duration: Math.random() * 8 + 8,
                repeat: Infinity,
                delay: Math.random() * 8,
                ease: [0.4, 0, 0.2, 1],
                times: [0, 0.5, 1]
              }}
            />
          );
        })}

        {/* Marquee */}
        <div className="absolute top-6 sm:top-8 md:top-10 w-full overflow-hidden">
          <motion.div
            className="whitespace-nowrap text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light italic font-sans text-black"
            initial={{ x: "100%" }}
            animate={{ x: "-100%" }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            Mobile App Design · Interaction Design · Visual Storytelling · Mobile App Design · Interaction Design · Visual Storytelling ·
          </motion.div>
        </div>

        {/* Rotating images */}
        <div className="relative flex items-center justify-center z-10">
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="absolute -top-44 sm:-top-48 md:-top-56 w-[300px] sm:w-[400px] md:w-[500px] lg:w-[580px] h-[300px] sm:h-[400px] md:h-[500px] lg:h-[580px] z-20"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImageIndex}
                src={designImages[currentImageIndex]}
                alt={`Witness Design ${currentImageIndex + 1}`}
                className="w-full h-full object-contain"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              />
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.section>

      {/* Project Overview Section */}
      <motion.section
        className="w-full bg-[#F4F1EC] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Meta — right aligned like SlowisFast */}
          <div className="flex flex-wrap gap-12 mb-16 border-b border-gray-300 pb-10">
            {[
              { label: "Role", value: "UI/UX Designer · Visual Designer" },
              { label: "Type", value: "Mobile Application" },
              { label: "Focus", value: "Visual Design · Interface Design" },
            ].map((m) => (
              <div key={m.label}>
                <p className="text-xs uppercase tracking-widest mb-1 font-sans text-gray-500">{m.label}</p>
                <p className="text-black font-medium font-sans">{m.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Overview</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold mb-6">Project Overview</h2>
              <div className="space-y-5 text-lg leading-relaxed text-gray-700 font-sans">
                <p>
                  Witness is a community-driven urban legend discovery app. Users explore a live map of reported sightings, browse local legends by category, and contribute their own stories — turning folklore into a shared, location-based experience.
                </p>
              </div>
              <ul className="mt-8 space-y-3">
                {[
                  "Location-based map showing nearby urban legend sightings in real time",
                  "Category browsing across Cryptids, Hauntings, True Crime, and Legends",
                  "Community storytelling — users can add, react to, and collect legends",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-lg text-gray-700 font-sans">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F0584E] mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-start justify-center">
              <div className="w-1/2 overflow-hidden rounded-2xl">
                <img
                  src="/wit-start.png"
                  alt="Witness App Start"
                  className="w-full scale-[1.12] object-cover"
                />
              </div>
              <div className="w-1/2 overflow-hidden rounded-2xl -ml-14">
                <img
                  src="/wit-map.png"
                  alt="Witness App Map"
                  className="w-full scale-[1.12] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Design Challenge Section */}
      <motion.section
        className="w-full bg-[#1E1E1E] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-4">Challenge</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white mb-8">
            Atmosphere vs. Usability
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div className="space-y-5 text-lg leading-relaxed text-gray-300 font-sans">
              <p>
                The core design challenge was making a dark, folklore-driven app feel genuinely eerie — without making it hard to use. Horror aesthetics often work against clarity: low contrast, decorative type, and dense textures can obscure navigation and frustrate users.
              </p>
              <p>
                The goal was to find a visual language where atmosphere and function reinforce each other — where the UI itself feels like part of the legend.
              </p>
            </div>
            <div className="space-y-6">
              {[
                { n: "01", body: "Dark UI that still meets readability and accessibility standards" },
                { n: "02", body: "A community-driven experience that feels curated, not chaotic" },
                { n: "03", body: "Location-based features that work intuitively without onboarding friction" },
              ].map((item) => (
                <div key={item.n} className="flex gap-6 items-start">
                  <span className="text-[#F0584E] font-serif font-bold text-2xl flex-shrink-0">{item.n}</span>
                  <p className="text-gray-300 font-sans text-lg leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Key Features Section */}
      <motion.section
        className="w-full bg-[#F4F1EC] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Features</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold mb-10">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", title: "Dark by Design", body: "Every visual decision — contrast, type, motion — is in service of atmosphere. The UI doesn't just look dark. It feels like something is watching back." },
              { n: "02", title: "Stories That Move", body: "Branching paths, reactive animations, and gesture-driven reveals. Users don't scroll through content — they uncover it." },
              { n: "03", title: "Fear Has No Barrier", body: "Adjustable text, voice narration, and accessible interactions — because the mystery should be open to everyone who dares to look." },
            ].map((item) => (
              <div key={item.n} className="border border-black p-8">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-6">{item.n}</p>
                <h4 className="text-2xl font-serif mb-4">{item.title}</h4>
                <p className="text-gray-600 font-sans leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="relative w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div 
          className="max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={itemVariants} className="mb-12 sm:mb-16 md:mb-20">
            <div className="w-40 sm:w-60 md:w-80 h-[2px] bg-[#F0584E] mb-4 mt-4 sm:mt-6 md:mt-8"></div>
            <motion.div variants={containerVariants} className="grid gap-8 sm:gap-12 md:gap-16">
              <motion.div variants={itemVariants}>
                <h3 
                  style={{ 
                    fontFamily: "'Jolly Lodger', cursive",
                    fontSize: "clamp(3rem, 10vw, 8rem)",
                    letterSpacing: "0.25em",
                    marginBottom: "1rem",
                    fontStyle: "italic"
                  }}
                >
                  AaBbCcDdEeFfGgHhIiJjKkLl
                </h3>
                <p
                  style={{
                    fontFamily: "'Jolly Lodger', cursive",
                    fontSize: "clamp(1.2rem, 1.5rem, 2rem)",
                    lineHeight: "1.75rem",
                    color: "rgb(75 85 99)",
                    marginLeft: "clamp(0rem, 5vw, 6rem)",
                    fontStyle: "italic"
                  }}
                >
                  Jolly Lodger
                </p>
              </motion.div>
              <motion.div variants={itemVariants}>
                <h3 
                  style={{ 
                    fontFamily: "'Mina', sans-serif",
                    fontSize: "clamp(2rem, 8vw, 6rem)",
                    letterSpacing: "0.25em",
                    marginBottom: "1rem",
                    fontStyle: "italic"
                  }}
                >
                  AaBbCcDdEeFfGgHhIiJjKkLl
                </h3>
                <p
                  style={{
                    fontFamily: "'Mina', sans-serif",
                    fontSize: "clamp(1rem, 1.25rem, 1.5rem)",
                    lineHeight: "1.75rem",
                    color: "rgb(75 85 99)",
                    marginLeft: "clamp(0rem, 5vw, 6rem)",
                    fontStyle: "italic"
                  }}
                >
                  Mina
                </p>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div variants={containerVariants} className="flex flex-col md:flex-row w-full gap-4 sm:gap-6 md:gap-8">
            <motion.div variants={itemVariants} className="w-full md:w-[40%] grid grid-cols-2 gap-4 sm:gap-6 md:gap-10">
              <motion.div 
                variants={itemVariants}
                className="w-full h-32 sm:h-36 md:h-40 bg-[#1E1E1E] relative rounded-[16px]"
              >
                <span className="absolute bottom-1 left-1 text-white text-xs">#1E1E1E</span>
              </motion.div>
              <motion.div 
                variants={itemVariants}
                className="w-full h-32 sm:h-36 md:h-40 bg-[#F0584E] relative rounded-[16px]"
              >
                <span className="absolute bottom-1 left-1 text-white text-xs">#F0584E</span>
              </motion.div>
              <motion.div 
                variants={itemVariants}
                className="w-full h-32 sm:h-36 md:h-40 bg-[#F2F2F2] border border-black relative rounded-[16px]"
              >
                <span className="absolute bottom-1 left-1 text-black text-xs">#F2F2F2</span>
              </motion.div>
              <motion.div 
                variants={itemVariants}
                className="w-full h-32 sm:h-36 md:h-40 bg-[#E8E4DD] border border-black relative rounded-[16px]"
              >
                <span className="absolute bottom-1 left-1 text-black text-xs">#E8E4DD</span>
              </motion.div>
            </motion.div>
            <motion.div 
              variants={itemVariants}
              className="w-full md:w-[60%] h-[240px] sm:h-[300px] md:h-[360px] bg-[#F0584E] flex items-center justify-center relative rounded-[16px]"
            >
              <img 
                src="/eye-white.png" 
                alt="White Eye" 
                className="w-40 sm:w-48 md:w-60 h-40 sm:h-48 md:h-60 object-contain"
              />
              <span className="absolute bottom-1 left-1 text-white text-xs">#EA4335</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Screen Gallery */}
      <section className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Screens</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold mb-6">Screen Gallery</h2>
          <p className="text-lg text-gray-600 font-sans leading-relaxed mb-16">
            The following screens cover the full user journey — from first launch through exploration, contribution, and discovery. Each screen was designed to balance dark atmosphere with clear, functional UI.
          </p>

          <div className="space-y-20">
            {[
              {
                src: "/w-all1.png",
                label: "Onboarding",
                title: "Sign In · Sign Up · Category Selection",
                desc: "The entry flow is designed to be minimal and atmospheric. Users sign in with existing credentials or register with a full name, email, and password. The dark visual language is established immediately — the Witness logo and dripping eye icon set the tone before a single feature is introduced. After authentication, users are prompted to select their legend categories: Cryptids, Hauntings, True Crime, or Legends. This single step personalizes the map experience from the very first session, surfacing the most relevant sightings without requiring any further setup.",
              },
              {
                src: "/w-all2.png",
                label: "Map",
                title: "Live Legend Map · Active Sighting",
                desc: "The map is the heart of Witness. Populated with location-pinned sightings from the community, it gives users a real-time view of what has been reported nearby. Each pin carries the legend's icon and name — Mothman, Urban Legend #2 — making the map feel alive and specific rather than generic. When a sighting is selected, it surfaces as an active card, highlighting the location and inviting the user to explore further. The navigation bar at the bottom keeps core actions always within reach: browse, add, collect, profile.",
              },
              {
                src: "/w-all3.png",
                label: "Content",
                title: "Legend Detail · Add Story · Legend Profile",
                desc: "Each legend has a dedicated profile card with a hand-drawn illustration, category tag, description, and community reaction options — I love it, I see it, I knew it. The discussion thread below lets users share their own encounters. The Add Story form allows anyone to contribute a new legend, filling in a name, written account, image or video, category, and auto-detected location. The legend profile page acts as a collectible entry — once discovered or confirmed, a legend is added to the user's personal collection.",
              },
              {
                src: "/w-all4.png",
                label: "Discovery",
                title: "Urban News · AR View · Caught Screen",
                desc: "The Urban News feed aggregates community-submitted legend reports from around the city, each tagged with a location and illustrated header. The AR view is where the experience becomes truly immersive — cryptid sightings are overlaid onto a live camera feed, anchored to real-world geography. Users can physically move through space to find and confirm sightings. When a legend is confirmed, a 'Caught you' screen celebrates the moment, locking the legend into the user's collection and reinforcing the sense of discovery that drives the app.",
              },
            ].map((item) => (
              <div key={item.label} className="flex flex-col gap-6">
                <img src={item.src} alt={item.title} className="w-4/5 mx-auto rounded-2xl" />
                <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-8">
                  <div className="md:border-r-2 md:border-dashed md:border-[#F0584E] md:pr-8">
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-2">{item.label}</p>
                    <p className="font-serif text-xl font-semibold text-black">{item.title}</p>
                  </div>
                  <p className="text-gray-600 font-sans text-lg leading-relaxed md:pl-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <motion.section
        className="w-full bg-[#1E1E1E] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-4">Reflection</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white leading-snug">
              Design is most powerful when it makes the invisible feel real.
            </h2>
            <div className="space-y-5 text-gray-300 font-sans text-lg leading-relaxed">
              <p>
                Witness started with a simple question: what if urban legends had a home? Not a wiki, not a subreddit — a designed experience built around place, atmosphere, and community.
              </p>
              <p>
                The biggest design challenge wasn't the dark aesthetic. It was making a folklore app feel trustworthy enough for people to contribute their own stories. Every interaction — the category selection, the Add Story form, the reaction buttons — was designed to lower that barrier.
              </p>
              <p>
                If there's one thing this project reinforced, it's that atmosphere and usability aren't opposites. When the design is intentional, they make each other stronger.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      <div className="flex justify-center mt-12 sm:mt-16 md:mt-20 mb-20 sm:mb-32 md:mb-40">
        <Link 
          to="/all-projects"
          onClick={() => {
            window.scrollTo(0, 0);
          }}
          className="bg-[#EA4335]/90 text-white border border-black/90 px-12 sm:px-16 md:px-20 py-3 sm:py-4 font-serif text-sm sm:text-base font-medium transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-[0_8px_0_0_rgba(0,0,0,0.8)] hover:bg-[#F0584E] active:translate-y-0 active:shadow-[0_1px_0_0_rgba(0,0,0,0.8)] rounded-[20px]"
        >
          VIEW ALL PROJECTS
        </Link>
      </div>

      <Footer />
    </div>
  );
};

export default Witness; 